import { format, formatDistanceToNow, isValid, parseISO } from "date-fns";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

// ---------------------------------------------------------------------------
// Class name merger (clsx + tailwind-merge — required by shadcn/ui)
// ---------------------------------------------------------------------------

/**
 * Merge multiple class name values into a single string, resolving Tailwind
 * conflicts via tailwind-merge and handling conditionals via clsx.
 *
 * @example
 *   cn('px-2 py-1', isActive && 'bg-blue-500', 'px-4')
 *   // => 'py-1 bg-blue-500 px-4'  (px-2 is overridden by px-4)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

/**
 * Format an ISO date string (or Date object) into a human-readable string.
 * Falls back to a safe empty string if the date is invalid.
 *
 * @param date      — ISO 8601 string or Date instance
 * @param fmt       — date-fns format string (default: "MMM d, yyyy")
 * @param _timezone — reserved for future use (date-fns-tz integration)
 *
 * @example
 *   formatDate('2024-03-15T10:30:00Z')
 *   // => 'Mar 15, 2024'
 *
 *   formatDate('2024-03-15T10:30:00Z', 'dd/MM/yyyy HH:mm')
 *   // => '15/03/2024 10:30'
 */
export function formatDate(
  date: string | Date | null | undefined,
  fmt = "MMM d, yyyy",
  _timezone?: string,
): string {
  if (!date) return "";

  const parsed = typeof date === "string" ? parseISO(date) : date;

  if (!isValid(parsed)) return "";

  try {
    return format(parsed, fmt);
  } catch {
    return "";
  }
}

/**
 * Format a date + time string into a compact display format.
 *
 * @example
 *   formatDateTime('2024-03-15T10:30:00Z')
 *   // => 'Mar 15, 2024 10:30 AM'
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  return formatDate(date, "MMM d, yyyy h:mm a");
}

/**
 * Format a date as a short date string.
 *
 * @example
 *   formatShortDate('2024-03-15T10:30:00Z')
 *   // => '03/15/2024'
 */
export function formatShortDate(
  date: string | Date | null | undefined,
): string {
  return formatDate(date, "MM/dd/yyyy");
}

/**
 * Format a date relative to now (e.g. "2 days ago", "in 3 hours").
 * Falls back to the absolute date if parsing fails.
 *
 * @example
 *   formatRelativeDate('2024-03-13T10:30:00Z')
 *   // => '2 days ago'
 */
export function formatRelativeDate(
  date: string | Date | null | undefined,
): string {
  if (!date) return "";

  const parsed = typeof date === "string" ? parseISO(date) : date;

  if (!isValid(parsed)) return "";

  try {
    return formatDistanceToNow(parsed, { addSuffix: true });
  } catch {
    return formatDate(date);
  }
}

// ---------------------------------------------------------------------------
// String helpers
// ---------------------------------------------------------------------------

/**
 * Truncate a string to `maxLength` characters, appending an ellipsis if it
 * was actually shortened. Safe to call with null / undefined.
 *
 * @example
 *   truncate('Hello, world!', 8)
 *   // => 'Hello, w…'
 *
 *   truncate('Short', 10)
 *   // => 'Short'
 */
export function truncate(
  str: string | null | undefined,
  maxLength: number,
): string {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength)}…`;
}

/**
 * Capitalise the first letter of a string and lowercase the rest.
 *
 * @example
 *   capitalize('hELLO')
 *   // => 'Hello'
 */
export function capitalize(str: string | null | undefined): string {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Convert a camelCase or snake_case identifier to a human-readable title.
 *
 * @example
 *   toTitle('utmSource')    // => 'Utm Source'
 *   toTitle('max_redirects') // => 'Max Redirects'
 */
export function toTitle(str: string): string {
  return str
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^\s/, "")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ---------------------------------------------------------------------------
// Clipboard
// ---------------------------------------------------------------------------

/**
 * Copy a text string to the user's clipboard.
 * Returns `true` on success and `false` if the clipboard API is unavailable
 * or the user denied permission.
 *
 * @example
 *   const ok = await copyToClipboard('https://cliqo.link/abc123')
 *   if (ok) toast.success('Copied!')
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  // Modern Clipboard API (requires HTTPS or localhost)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to legacy approach
    }
  }

  // Legacy execCommand fallback for older browsers / HTTP contexts
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.top = "-9999px";
    textarea.setAttribute("readonly", "");
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const success = document.execCommand("copy");
    document.body.removeChild(textarea);
    return success;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Number / currency helpers
// ---------------------------------------------------------------------------

/**
 * Format a monetary amount using the Intl API.
 *
 * @example
 *   formatMoney(1999, 'USD')  // => '$19.99'  (amount is in cents)
 *   formatMoney(999,  'EUR')  // => '€9.99'
 */
export function formatMoney(
  amountInCents: number,
  currency = "USD",
  locale = "en-US",
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(amountInCents / 100);
  } catch {
    return `${(amountInCents / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Format a large number with compact notation (e.g. 1_500 → "1.5K").
 *
 * @example
 *   formatCompactNumber(1500)    // => '1.5K'
 *   formatCompactNumber(2000000) // => '2M'
 */
export function formatCompactNumber(value: number, locale = "en-US"): string {
  try {
    return new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  } catch {
    return String(value);
  }
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/**
 * Extract just the hostname from a full URL string.
 * Falls back to the original string if parsing fails.
 *
 * @example
 *   getDomain('https://www.example.com/path?q=1')
 *   // => 'www.example.com'
 */
export function getDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/**
 * Ensure a URL string starts with a protocol (defaults to https://).
 * Useful when storing user-supplied URLs that may omit the scheme.
 *
 * @example
 *   ensureProtocol('example.com')
 *   // => 'https://example.com'
 */
export function ensureProtocol(url: string, protocol = "https://"): string {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `${protocol}${url}`;
}

// ---------------------------------------------------------------------------
// Array / object helpers
// ---------------------------------------------------------------------------

/**
 * Remove duplicate values from an array using strict equality.
 */
export function unique<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}

/**
 * Group an array of objects by a string key.
 *
 * @example
 *   groupBy([{type:'a',v:1},{type:'b',v:2},{type:'a',v:3}], 'type')
 *   // => { a: [{type:'a',v:1},{type:'a',v:3}], b: [{type:'b',v:2}] }
 */
export function groupBy<T extends Record<string, unknown>>(
  arr: T[],
  key: keyof T,
): Record<string, T[]> {
  return arr.reduce<Record<string, T[]>>((acc, item) => {
    const group = String(item[key]);
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/**
 * Sleep for a given number of milliseconds.
 * Useful for testing loading states or intentional delays.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate a simple random string suitable for use as a temporary client-side ID.
 * NOT cryptographically secure — do not use for security-sensitive purposes.
 */
export function randomId(length = 8): string {
  return Math.random()
    .toString(36)
    .substring(2, 2 + length);
}

/**
 * Check whether a value is a non-empty string.
 */
export function isNonEmpty(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

/**
 * Safely parse a JSON string, returning null on failure instead of throwing.
 */
export function safeJsonParse<T = unknown>(json: string): T | null {
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}
